import type { SceneAsset, Listing, Account } from "@/types"
import type { ScriptContent, TextPosition, TextAlign, SpecialTransitionStyle, LogoSize } from "./constants"
import {
  DEFAULT_TITLE_CARD_DURATION,
  FONT_OPTIONS,
  FONT_SIZE_OPTIONS,
  FONT_WEIGHT_OPTIONS,
  COLOUR_PRESETS,
  SPECIAL_TRANSITION_OPTIONS,
  MAX_CONCURRENT_ANIMATIONS,
} from "./constants"
import {
  ScaledFrame,
  SceneComposition,
  TitleComposition,
  ClosingComposition,
} from "./composition-preview"
import { SceneActions } from "./scene-action-toolbar"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useEstimatedProgress } from "@/hooks/use-estimated-progress"

type StyleUpdate = {
  text_position?: TextPosition
  text_align?: TextAlign
  text_font?: string
  text_size?: string
  text_colour?: string
  text_uppercase?: boolean
  text_weight?: string
  show_branding?: boolean
  show_cta?: boolean
  show_cta_button?: boolean
  cta_button_text_colour?: string
  title_bg_gradient?: "none" | "linear-top" | "linear-bottom" | "radial"
  title_bg_gradient_end?: string
  title_logo_position?: "above" | "below" | "hidden"
  title_logo_size?: LogoSize
  title_transition_style?: SpecialTransitionStyle
}

export default function PreviewPanel({
  selectedIndex,
  content,
  aspectRatio,
  templateVariant,
  imageUrls,
  sceneAssets,
  sceneAssetStatuses,
  voiceoverCompleted,
  listing,
  account,
  isPlaying,
  onPlayAll,
  onStopPlayback,
  onUpdateScene,
  onUpdateHeadline,
  onUpdateTitleCardBg,
  onEnhanceScene,
  onAnimateScene,
  onSwapImage,
  onRevertScene,
  onUpdateSceneStyle,
  onUpdateTitleStyle,
  onUpdateClosingStyle,
  onApplyStyleToAll,
}: {
  selectedIndex: number
  content: ScriptContent | undefined
  aspectRatio: string
  templateVariant: string
  imageUrls: string[]
  sceneAssets: SceneAsset[]
  sceneAssetStatuses: Record<string, { status: SceneAsset["status"]; message?: string; progress?: number }>
  voiceoverCompleted: boolean
  listing: Listing
  account: Account
  isPlaying: boolean
  onPlayAll: () => void
  onStopPlayback: () => void
  onUpdateScene: (index: number, text: string) => void
  onUpdateHeadline: (text: string) => void
  onUpdateTitleCardBg: (bg: { colour?: string }) => void
  onEnhanceScene: (index: number) => void
  onAnimateScene: (index: number) => void
  onSwapImage: (index: number) => void
  onRevertScene: (index: number) => void
  onUpdateSceneStyle?: (index: number, style: StyleUpdate) => void
  onUpdateTitleStyle?: (style: StyleUpdate) => void
  onUpdateClosingStyle?: (style: ClosingStyleUpdate) => void
  onApplyStyleToAll?: (style: StyleUpdate) => void
}) {
  const contentScenes = content?.scenes?.filter((s) => s.type !== "closing") || []
  const closingScene = content?.scenes?.find((s) => s.type === "closing")

  const brandColour = account.brand_colour || "#C41E2A"

  const titleBgColour = content?.title_card_bg_colour || brandColour

  const isTitle = selectedIndex === -1
  const isClosing = selectedIndex === contentScenes.length
  const isContent = selectedIndex >= 0 && selectedIndex < contentScenes.length
  const scene = isContent ? contentScenes[selectedIndex] : undefined

  // Preview media for content scenes
  const previewMedia = isContent ? getPreviewMedia(selectedIndex, sceneAssets, imageUrls) : null

  // Scene asset info — content scenes only (title no longer has assets)
  const canHaveAssets = isContent
  const enhanceKey = `${selectedIndex}-enhanced_image`
  const animateKey = `${selectedIndex}-animated_clip`
  const enhanceStatus = canHaveAssets ? sceneAssetStatuses[enhanceKey]?.status : undefined
  const animateStatus = canHaveAssets ? sceneAssetStatuses[animateKey]?.status : undefined
  const enhancedAsset = canHaveAssets
    ? sceneAssets.find((sa) => sa.scene_index === selectedIndex && sa.asset_type === "enhanced_image")
    : undefined
  const animatedAsset = canHaveAssets
    ? sceneAssets.find((sa) => sa.scene_index === selectedIndex && sa.asset_type === "animated_clip")
    : undefined
  const isEnhancing = enhanceStatus === "generating"
  const isAnimating = animateStatus === "generating"
  const animatingCount = Object.entries(sceneAssetStatuses).filter(
    ([key, value]) => key.endsWith("-animated_clip") && value.status === "generating",
  ).length
  const animateCapReached = animatingCount >= MAX_CONCURRENT_ANIMATIONS
  const isEnhanced = !!enhancedAsset?.file_url && enhancedAsset?.status === "completed"
  const isAnimated = !!animatedAsset?.file_url && animatedAsset?.status === "completed"
  const hasAnyAsset = isEnhanced || isAnimated || isEnhancing || isAnimating
  const processing = isEnhancing || isAnimating
  const enhanceFailed = enhanceStatus === "failed"
  const animateFailed = animateStatus === "failed"

  const enhanceProgress = useEstimatedProgress({
    isActive: isEnhancing,
    serverProgress: sceneAssetStatuses[enhanceKey]?.progress,
    estimatedDuration: 25,
  })
  const animateProgress = useEstimatedProgress({
    isActive: isAnimating,
    serverProgress: sceneAssetStatuses[animateKey]?.progress,
    estimatedDuration: 90,
  })
  const activeProgress = isAnimating ? animateProgress : enhanceProgress

  const titleDuration = content?.title_card_duration ?? DEFAULT_TITLE_CARD_DURATION
  const closingDuration = closingScene?.duration_seconds ?? 4

  // Text style for current scene preview
  const sceneTextPosition = scene?.text_position ?? "bottom"
  const sceneTextAlign = scene?.text_align ?? "center"
  const sceneTextFont = scene?.text_font
  const sceneTextSize = scene?.text_size
  const sceneTextColour = scene?.text_colour
  const sceneTextWeight = scene?.text_weight

  // Title text style
  const titleTextPosition = content?.title_text_position ?? "bottom"
  const titleTextAlign = content?.title_text_align ?? "center"
  const titleTextFont = content?.title_text_font
  const titleTextSize = content?.title_text_size
  const titleTextColour = content?.title_text_colour
  const titleTextWeight = content?.title_text_weight

  // CTA
  const sceneShowCta = scene?.show_cta ?? true
  const sceneShowCtaButton = scene?.show_cta_button ?? true
  const ctaText = content?.cta_text || "Book a viewing"

  return (
    <div className="overflow-hidden rounded-lg border border-stone/30 bg-white">
      <div className="flex flex-col gap-4 p-3 sm:p-4 md:flex-row">
        {/* Left: Scene preview + action buttons */}
        <div className="w-full shrink-0 md:w-[58%]">
          <div className="flex items-start justify-center gap-1.5">
          <div className={`relative overflow-hidden rounded-lg bg-charcoal/5 ring-1 ring-stone/20 ${aspectRatio === "portrait" ? "w-[300px] max-w-full shrink-0" : aspectRatio === "square" ? "w-full max-w-[380px]" : "min-w-0 flex-1"}`}>
            <ScaledFrame aspectRatio={aspectRatio}>
              {isTitle && (
                <TitleComposition
                  headline={content?.headline}
                  logoUrl={account.logo_url}
                  bgColour={titleBgColour}
                  bgGradient={content?.title_bg_gradient}
                  bgGradientEnd={content?.title_bg_gradient_end}
                  logoPosition={content?.title_logo_position}
                  logoSize={content?.title_logo_size}
                  templateVariant={templateVariant}
                  textPosition={titleTextPosition}
                  textAlign={titleTextAlign}
                  textFont={titleTextFont}
                  textSize={titleTextSize}
                  textColour={titleTextColour}
                  textUppercase={content?.title_text_uppercase}
                  textWeight={titleTextWeight}
                  aspectRatio={aspectRatio}
                />
              )}

              {isContent && (
                <SceneComposition
                  text={(!processing && scene?.text) || ""}
                  mediaUrl={previewMedia?.url}
                  mediaType={previewMedia?.type}
                  templateVariant={templateVariant}
                  textPosition={sceneTextPosition}
                  textAlign={sceneTextAlign}
                  textFont={sceneTextFont}
                  textSize={sceneTextSize}
                  textColour={sceneTextColour}
                  textUppercase={scene?.text_uppercase}
                  textWeight={sceneTextWeight}
                  showCta={sceneShowCta}
                  showCtaButton={sceneShowCtaButton}
                  ctaButtonTextColour={content?.cta_button_text_colour}
                  account={account}
                  ctaText={ctaText}
                  aspectRatio={aspectRatio}
                />
              )}

              {isClosing && (
                <ClosingComposition
                  listing={listing}
                  account={account}
                  closingBgColour={content?.closing_bg_colour ?? undefined}
                  closingBgGradient={content?.closing_bg_gradient}
                  closingBgGradientEnd={content?.closing_bg_gradient_end}
                  closingFont={content?.closing_text_font}
                  closingSize={content?.closing_text_size}
                  closingColour={content?.closing_text_colour}
                  closingUppercase={content?.closing_text_uppercase}
                  closingWeight={content?.closing_text_weight}
                  closingLogoSize={content?.closing_logo_size}
                  showWatermark={content?.show_watermark ?? true}
                  aspectRatio={aspectRatio}
                />
              )}
            </ScaledFrame>

            {/* Processing overlay */}
            {canHaveAssets && processing && (
              <>
                <div className="absolute inset-x-0 top-0 z-20 h-1 bg-charcoal/30">
                  <div
                    className="h-full bg-red transition-all duration-300 ease-out"
                    style={{ width: `${activeProgress.progress * 100}%` }}
                  />
                </div>
                <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-charcoal/60">
                  <svg className="size-6 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="mt-2 text-xs font-medium text-white/90">
                    {sceneAssetStatuses[isEnhancing ? enhanceKey : animateKey]?.message
                      || (isEnhancing ? "Enhancing..." : "Animating...")}
                  </span>
                </div>
              </>
            )}

            {/* Play/pause button overlay */}
            {(isTitle || isContent || isClosing) && !processing && (
              <button
                type="button"
                onClick={() => isPlaying ? onStopPlayback() : onPlayAll()}
                className="group absolute inset-0 z-20 flex items-center justify-center bg-charcoal/0 transition-all hover:bg-charcoal/20"
              >
                <div className={`rounded-full p-2 shadow-sm transition-all ${isPlaying ? "bg-red text-white opacity-0 group-hover:opacity-100" : "bg-white/90 text-charcoal opacity-0 group-hover:opacity-100"}`}>
                  {isPlaying ? (
                    <svg className="size-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                    </svg>
                  ) : (
                    <svg className="size-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </div>
              </button>
            )}

            {/* Scene badge */}
            {isContent && !processing && (
              <div className="absolute left-2 top-2 z-20 flex size-6 items-center justify-center rounded-full bg-charcoal/80 text-[10px] font-bold text-white shadow">
                {selectedIndex + 1}
              </div>
            )}
            {isTitle && (
              <div className="absolute left-2 top-2 z-20 rounded bg-charcoal/80 px-1.5 py-0.5 text-[9px] font-semibold text-white shadow">
                Title
              </div>
            )}

          </div>
          {/* Scene action buttons — desktop: vertical beside preview */}
          <div className="hidden md:flex">
            {canHaveAssets && (
              <SceneActions
                isEnhanced={isEnhanced}
                isAnimated={isAnimated}
                isEnhancing={isEnhancing}
                isAnimating={isAnimating}
                enhanceFailed={enhanceFailed}
                animateFailed={animateFailed}
                voiceoverCompleted={voiceoverCompleted}
                hasAnyAsset={hasAnyAsset}
                animateCapReached={animateCapReached}
                onEnhance={() => onEnhanceScene(selectedIndex)}
                onAnimate={() => onAnimateScene(selectedIndex)}
                onSwapImage={() => onSwapImage(selectedIndex)}
                onRevert={() => onRevertScene(selectedIndex)}
              />
            )}
            {(isTitle || isClosing) && (
              <SceneActions
                disabled
                isEnhanced={false}
                isAnimated={false}
                isEnhancing={false}
                isAnimating={false}
                enhanceFailed={false}
                animateFailed={false}
                voiceoverCompleted={false}
                hasAnyAsset={false}
                onEnhance={() => {}}
                onAnimate={() => {}}
                onSwapImage={() => {}}
                onRevert={() => {}}
              />
            )}
          </div>
          </div>
          {/* Scene action buttons — mobile: horizontal below preview */}
          <div className="mt-2 flex md:hidden">
            {canHaveAssets && (
              <SceneActions
                horizontal
                isEnhanced={isEnhanced}
                isAnimated={isAnimated}
                isEnhancing={isEnhancing}
                isAnimating={isAnimating}
                enhanceFailed={enhanceFailed}
                animateFailed={animateFailed}
                voiceoverCompleted={voiceoverCompleted}
                hasAnyAsset={hasAnyAsset}
                animateCapReached={animateCapReached}
                onEnhance={() => onEnhanceScene(selectedIndex)}
                onAnimate={() => onAnimateScene(selectedIndex)}
                onSwapImage={() => onSwapImage(selectedIndex)}
                onRevert={() => onRevertScene(selectedIndex)}
              />
            )}
            {(isTitle || isClosing) && (
              <SceneActions
                horizontal
                disabled
                isEnhanced={false}
                isAnimated={false}
                isEnhancing={false}
                isAnimating={false}
                enhanceFailed={false}
                animateFailed={false}
                voiceoverCompleted={false}
                hasAnyAsset={false}
                onEnhance={() => {}}
                onAnimate={() => {}}
                onSwapImage={() => {}}
                onRevert={() => {}}
              />
            )}
          </div>
        </div>

        {/* Right: Inspector */}
        <div className="flex min-w-0 flex-1 flex-col rounded-lg border border-stone/15 bg-cream/30 p-3 sm:p-4">
          {isTitle && (
            <TitleInspector
              content={content}
              titleBgColour={titleBgColour}
              brandColour={brandColour}
              titleDuration={titleDuration}
              onUpdateHeadline={onUpdateHeadline}
              onUpdateTitleCardBg={onUpdateTitleCardBg}
              onUpdateStyle={onUpdateTitleStyle}
            />
          )}

          {isContent && scene && (
            <ContentInspector
              sceneIndex={selectedIndex}
              scene={scene}
              ctaButtonTextColour={content?.cta_button_text_colour}
              onUpdateScene={onUpdateScene}
              onUpdateStyle={onUpdateSceneStyle ? (style) => onUpdateSceneStyle(selectedIndex, style) : undefined}
              onApplyStyleToAll={onApplyStyleToAll}
            />
          )}

          {isClosing && (
            <ClosingInspector
              content={content}
              closingDuration={closingDuration}
              closingBgColour={content?.closing_bg_colour ?? undefined}
              brandColour={brandColour}
              closingFont={content?.closing_text_font}
              closingSize={content?.closing_text_size}
              closingWeight={content?.closing_text_weight}
              closingColour={content?.closing_text_colour}
              closingUppercase={content?.closing_text_uppercase}
              onUpdateStyle={onUpdateClosingStyle}
            />
          )}
        </div>
      </div>

      {/* Error callout for failed scene operations */}
      {canHaveAssets && (enhanceFailed || animateFailed) && (
        <div className="flex items-center gap-1.5 border-t border-stone/20 px-4 py-2">
          <svg className="size-3 shrink-0 text-red" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
          </svg>
          <span className="text-[10px] text-charcoal">
            {enhanceFailed ? "Enhance" : "Animate"} failed — try again or revert.
          </span>
        </div>
      )}
    </div>
  )
}

function PositionToggle({
  value,
  onChange,
}: {
  value: TextPosition
  onChange: (position: TextPosition) => void
}) {
  const options: Array<{ id: TextPosition; label: string }> = [
    { id: "top", label: "Top" },
    { id: "middle", label: "Middle" },
    { id: "bottom", label: "Bottom" },
  ]

  return (
    <div className="flex overflow-hidden rounded-md border border-stone/30 text-[10px] font-medium">
      {options.map((opt, i) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          className={`px-2 py-1 transition-colors ${i > 0 ? "border-l border-stone/30" : ""} ${
            value === opt.id
              ? "bg-charcoal text-white"
              : "bg-white text-warm-grey hover:bg-stone/10"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function AlignToggle({
  value,
  onChange,
}: {
  value: TextAlign
  onChange: (align: TextAlign) => void
}) {
  const options: Array<{ id: TextAlign; label: string; icon: React.ReactNode }> = [
    {
      id: "left", label: "Left",
      icon: <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" d="M3 6h18M3 12h12M3 18h16" /></svg>,
    },
    {
      id: "center", label: "Centre",
      icon: <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" d="M3 6h18M6 12h12M4 18h16" /></svg>,
    },
    {
      id: "right", label: "Right",
      icon: <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" d="M3 6h18M9 12h12M5 18h16" /></svg>,
    },
  ]

  return (
    <div className="flex overflow-hidden rounded-md border border-stone/30 text-[10px] font-medium">
      {options.map((opt, i) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          title={opt.label}
          className={`px-2 py-1 transition-colors ${i > 0 ? "border-l border-stone/30" : ""} ${
            value === opt.id
              ? "bg-charcoal text-white"
              : "bg-white text-warm-grey hover:bg-stone/10"
          }`}
        >
          {opt.icon}
        </button>
      ))}
    </div>
  )
}

function FontSelect({
  value,
  onChange,
}: {
  value?: string
  onChange: (fontId: string) => void
}) {
  const selectedFont = FONT_OPTIONS.find((f) => f.id === value)

  return (
    <Select value={value || "_default"} onValueChange={(v) => onChange(v === "_default" ? "" : v)}>
      <SelectTrigger size="sm" className="h-7 min-w-0 gap-1 border-stone/30 px-2 text-[11px] shadow-none" style={selectedFont ? { fontFamily: selectedFont.family } : undefined}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent position="popper" className="max-h-60">
        <SelectItem value="_default" className="text-xs">Default</SelectItem>
        {FONT_OPTIONS.map((font) => (
          <SelectItem key={font.id} value={font.id} className="text-xs" style={{ fontFamily: font.family }}>
            {font.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function FontSizeSelect({
  value,
  onChange,
}: {
  value?: string
  onChange: (sizeId: string) => void
}) {
  return (
    <Select value={value || "_default"} onValueChange={(v) => onChange(v === "_default" ? "" : v)}>
      <SelectTrigger size="sm" className="h-7 min-w-0 gap-1 border-stone/30 px-2 text-[11px] shadow-none">
        <SelectValue />
      </SelectTrigger>
      <SelectContent position="popper">
        <SelectItem value="_default" className="text-xs">Default</SelectItem>
        {FONT_SIZE_OPTIONS.map((size) => (
          <SelectItem key={size.id} value={size.id} className="text-xs">
            {size.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function FontWeightSelect({
  value,
  onChange,
}: {
  value?: string
  onChange: (weightId: string) => void
}) {
  return (
    <Select value={value || "_default"} onValueChange={(v) => onChange(v === "_default" ? "" : v)}>
      <SelectTrigger size="sm" className="h-7 min-w-0 gap-1 border-stone/30 px-2 text-[11px] shadow-none">
        <SelectValue />
      </SelectTrigger>
      <SelectContent position="popper">
        <SelectItem value="_default" className="text-xs">Default</SelectItem>
        {FONT_WEIGHT_OPTIONS.map((weight) => (
          <SelectItem key={weight.id} value={weight.id} className="text-xs" style={{ fontWeight: weight.value }}>
            {weight.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function ColourPicker({
  value,
  onChange,
}: {
  value?: string
  onChange: (colour: string) => void
}) {
  return (
    <div className="flex items-center gap-1">
      {COLOUR_PRESETS.map((preset) => (
        <button
          key={preset.id}
          type="button"
          onClick={() => onChange(preset.value)}
          className={`size-5 rounded-full border-2 transition-all ${
            value === preset.value
              ? "border-charcoal ring-1 ring-charcoal/20"
              : "border-stone/40 hover:border-stone"
          }`}
          style={{ backgroundColor: preset.value }}
          title={preset.label}
        />
      ))}
      <label className="relative ml-1 cursor-pointer">
        <input
          type="color"
          value={value || "#FFFFFF"}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 size-0 opacity-0"
        />
        <div
          className={`flex size-5 items-center justify-center rounded-full border-2 transition-all ${
            value && !COLOUR_PRESETS.some((p) => p.value === value)
              ? "border-charcoal ring-1 ring-charcoal/20"
              : "border-stone/40 hover:border-stone"
          }`}
          style={{
            background: value && !COLOUR_PRESETS.some((p) => p.value === value)
              ? value
              : "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)",
          }}
          title="Custom colour"
        />
      </label>
    </div>
  )
}

function ApplyAllButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Apply to all scenes"
      className="ml-auto shrink-0 rounded px-1 py-0.5 text-[9px] text-warm-grey/50 transition-colors hover:bg-stone/15 hover:text-warm-grey"
    >
      Apply to all
    </button>
  )
}

function StyleControls({
  position,
  align,
  font,
  size,
  weight,
  colour,
  uppercase,
  showPosition = true,
  onChangePosition,
  onChangeAlign,
  onChangeFont,
  onChangeSize,
  onChangeWeight,
  onChangeColour,
  onChangeUppercase,
  onApplyToAll,
}: {
  position?: TextPosition
  align?: TextAlign
  font?: string
  size?: string
  weight?: string
  colour?: string
  uppercase?: boolean
  showPosition?: boolean
  onChangePosition?: (position: TextPosition) => void
  onChangeAlign?: (align: TextAlign) => void
  onChangeFont?: (fontId: string) => void
  onChangeSize?: (sizeId: string) => void
  onChangeWeight?: (weightId: string) => void
  onChangeColour?: (colour: string) => void
  onChangeUppercase?: (uppercase: boolean) => void
  onApplyToAll?: (style: StyleUpdate) => void
}) {
  return (
    <div className="mt-3 space-y-2 border-t border-stone/20 pt-3">
      <p className="text-[10px] font-medium uppercase tracking-wider text-warm-grey/70">
        Text style
      </p>
      {showPosition && onChangePosition && (
        <div className="flex items-center gap-2">
          <span className="w-12 shrink-0 text-[10px] text-warm-grey">Position</span>
          <PositionToggle value={position ?? "bottom"} onChange={onChangePosition} />
          {onApplyToAll && <ApplyAllButton onClick={() => onApplyToAll({ text_position: position ?? "bottom" })} />}
        </div>
      )}
      {onChangeAlign && (
        <div className="flex items-center gap-2">
          <span className="w-12 shrink-0 text-[10px] text-warm-grey">Align</span>
          <AlignToggle value={align ?? "center"} onChange={onChangeAlign} />
          {onApplyToAll && <ApplyAllButton onClick={() => onApplyToAll({ text_align: align ?? "center" })} />}
        </div>
      )}
      {onChangeFont && (
        <div className="flex items-center gap-2">
          <span className="w-12 shrink-0 text-[10px] text-warm-grey">Font</span>
          <FontSelect value={font} onChange={onChangeFont} />
          {onApplyToAll && <ApplyAllButton onClick={() => onApplyToAll({ text_font: font })} />}
        </div>
      )}
      {onChangeSize && (
        <div className="flex items-center gap-2">
          <span className="w-12 shrink-0 text-[10px] text-warm-grey">Size</span>
          <FontSizeSelect value={size} onChange={onChangeSize} />
          {onApplyToAll && <ApplyAllButton onClick={() => onApplyToAll({ text_size: size })} />}
        </div>
      )}
      {onChangeWeight && (
        <div className="flex items-center gap-2">
          <span className="w-12 shrink-0 text-[10px] text-warm-grey">Weight</span>
          <FontWeightSelect value={weight} onChange={onChangeWeight} />
          {onApplyToAll && <ApplyAllButton onClick={() => onApplyToAll({ text_weight: weight })} />}
        </div>
      )}
      {onChangeColour && (
        <div className="flex items-center gap-2">
          <span className="w-12 shrink-0 text-[10px] text-warm-grey">Colour</span>
          <ColourPicker value={colour} onChange={onChangeColour} />
          {onApplyToAll && <ApplyAllButton onClick={() => onApplyToAll({ text_colour: colour })} />}
        </div>
      )}
      {onChangeUppercase && (
        <div className="flex items-center gap-2">
          <span className="w-12 shrink-0 text-[10px] text-warm-grey">Caps</span>
          <button
            type="button"
            onClick={() => onChangeUppercase(!uppercase)}
            className={`rounded-md border px-2 py-1 text-[10px] font-medium transition-colors ${
              uppercase
                ? "border-charcoal bg-charcoal text-white"
                : "border-stone/30 bg-white text-warm-grey hover:bg-stone/10"
            }`}
          >
            ABC
          </button>
          {onApplyToAll && <ApplyAllButton onClick={() => onApplyToAll({ text_uppercase: uppercase ?? false })} />}
        </div>
      )}
    </div>
  )
}

function GradientToggle({
  value,
  onChange,
}: {
  value: "none" | "linear-top" | "linear-bottom" | "radial"
  onChange: (gradient: "none" | "linear-top" | "linear-bottom" | "radial") => void
}) {
  const options: Array<{ id: "none" | "linear-top" | "linear-bottom" | "radial"; label: string }> = [
    { id: "none", label: "Solid" },
    { id: "linear-top", label: "Top-down" },
    { id: "linear-bottom", label: "Bottom-up" },
    { id: "radial", label: "Radial" },
  ]

  return (
    <div className="flex overflow-hidden rounded-md border border-stone/30 text-[10px] font-medium">
      {options.map((opt, i) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          className={`px-2 py-1 transition-colors ${i > 0 ? "border-l border-stone/30" : ""} ${
            value === opt.id
              ? "bg-charcoal text-white"
              : "bg-white text-warm-grey hover:bg-stone/10"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function LogoPositionToggle({
  value,
  onChange,
}: {
  value: "above" | "below" | "hidden"
  onChange: (position: "above" | "below" | "hidden") => void
}) {
  const options: Array<{ id: "above" | "below" | "hidden"; label: string }> = [
    { id: "above", label: "Above" },
    { id: "below", label: "Below" },
    { id: "hidden", label: "Hidden" },
  ]

  return (
    <div className="flex overflow-hidden rounded-md border border-stone/30 text-[10px] font-medium">
      {options.map((opt, i) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          className={`px-2 py-1 transition-colors ${i > 0 ? "border-l border-stone/30" : ""} ${
            value === opt.id
              ? "bg-charcoal text-white"
              : "bg-white text-warm-grey hover:bg-stone/10"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function LogoSizeToggle({
  value,
  onChange,
}: {
  value: LogoSize
  onChange: (size: LogoSize) => void
}) {
  const options: Array<{ id: LogoSize; label: string }> = [
    { id: "small", label: "Small" },
    { id: "medium", label: "Medium" },
    { id: "large", label: "Large" },
  ]

  return (
    <div className="flex overflow-hidden rounded-md border border-stone/30 text-[10px] font-medium">
      {options.map((opt, i) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          className={`px-2 py-1 transition-colors ${i > 0 ? "border-l border-stone/30" : ""} ${
            value === opt.id
              ? "bg-charcoal text-white"
              : "bg-white text-warm-grey hover:bg-stone/10"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function TransitionStyleToggle({
  value,
  onChange,
}: {
  value: SpecialTransitionStyle
  onChange: (style: SpecialTransitionStyle) => void
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {SPECIAL_TRANSITION_OPTIONS.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          className={`rounded-md border px-2 py-1 text-[10px] font-medium transition-colors ${
            value === opt.id
              ? "border-charcoal bg-charcoal text-white"
              : "border-stone/30 bg-white text-warm-grey hover:bg-stone/10"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function TitleInspector({
  content,
  titleBgColour,
  brandColour,
  titleDuration,
  onUpdateHeadline,
  onUpdateTitleCardBg,
  onUpdateStyle,
}: {
  content: ScriptContent | undefined
  titleBgColour: string | undefined
  brandColour: string
  titleDuration: number
  onUpdateHeadline: (text: string) => void
  onUpdateTitleCardBg: (bg: { colour?: string }) => void
  onUpdateStyle?: (style: StyleUpdate) => void
}) {
  return (
    <div className="flex flex-1 flex-col">
      <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-warm-grey/70">
        Headline
      </p>
      <Textarea
        value={content?.headline || ""}
        onChange={(e) => onUpdateHeadline(e.target.value)}
        placeholder="Your headline text..."
        className="resize-none text-sm leading-relaxed text-slate"
        rows={2}
      />

      {/* Background colour */}
      <div className="mt-3 space-y-2">
        <p className="text-[10px] font-medium uppercase tracking-wider text-warm-grey/70">
          Background
        </p>
        <div className="flex items-center gap-1">
          {["#1A1A1A", brandColour, "#F5F2ED"].map((colour) => (
            <button
              key={colour}
              type="button"
              onClick={() => onUpdateTitleCardBg({ colour })}
              className={`size-5 rounded-full border-2 transition-all ${titleBgColour === colour ? "border-charcoal ring-1 ring-charcoal/20" : "border-stone/40 hover:border-stone"}`}
              style={{ backgroundColor: colour }}
              title={colour}
            />
          ))}
          <label className="relative ml-0.5 cursor-pointer">
            <input
              type="color"
              value={titleBgColour || "#1A1A1A"}
              onChange={(e) => onUpdateTitleCardBg({ colour: e.target.value })}
              className="absolute inset-0 size-0 opacity-0"
            />
            <div
              className={`flex size-5 items-center justify-center rounded-full border-2 transition-all ${
                titleBgColour && !["#1A1A1A", brandColour, "#F5F2ED"].includes(titleBgColour)
                  ? "border-charcoal ring-1 ring-charcoal/20"
                  : "border-stone/40 hover:border-stone"
              }`}
              style={{
                background: titleBgColour && !["#1A1A1A", brandColour, "#F5F2ED"].includes(titleBgColour)
                  ? titleBgColour
                  : "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)",
              }}
              title="Custom colour"
            />
          </label>
        </div>
      </div>

      {/* Gradient style */}
      {onUpdateStyle && (
        <div className="mt-3 flex items-center gap-2">
          <span className="w-12 shrink-0 text-[10px] text-warm-grey">Gradient</span>
          <GradientToggle
            value={content?.title_bg_gradient ?? "none"}
            onChange={(g) => onUpdateStyle({ title_bg_gradient: g } as StyleUpdate)}
          />
        </div>
      )}

      {/* Gradient end colour — only shown when a gradient is active */}
      {onUpdateStyle && content?.title_bg_gradient && content.title_bg_gradient !== "none" && (
        <div className="mt-2 space-y-1">
          <p className="text-[10px] font-medium uppercase tracking-wider text-warm-grey/70">
            Gradient end colour
          </p>
          <div className="flex items-center gap-1">
            {["#1A1A1A", "#000000", "#F5F2ED"].map((colour) => (
              <button
                key={colour}
                type="button"
                onClick={() => onUpdateStyle({ title_bg_gradient_end: colour } as StyleUpdate)}
                className={`size-5 rounded-full border-2 transition-all ${(content?.title_bg_gradient_end || "#1A1A1A") === colour ? "border-charcoal ring-1 ring-charcoal/20" : "border-stone/40 hover:border-stone"}`}
                style={{ backgroundColor: colour }}
                title={colour}
              />
            ))}
            <label className="relative ml-0.5 cursor-pointer">
              <input
                type="color"
                value={content?.title_bg_gradient_end || "#1A1A1A"}
                onChange={(e) => onUpdateStyle({ title_bg_gradient_end: e.target.value } as StyleUpdate)}
                className="absolute inset-0 size-0 opacity-0"
              />
              <div
                className={`flex size-5 items-center justify-center rounded-full border-2 transition-all ${
                  content?.title_bg_gradient_end && !["#1A1A1A", "#000000", "#F5F2ED"].includes(content.title_bg_gradient_end)
                    ? "border-charcoal ring-1 ring-charcoal/20"
                    : "border-stone/40 hover:border-stone"
                }`}
                style={{
                  background: content?.title_bg_gradient_end && !["#1A1A1A", "#000000", "#F5F2ED"].includes(content.title_bg_gradient_end)
                    ? content.title_bg_gradient_end
                    : "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)",
                }}
                title="Custom colour"
              />
            </label>
          </div>
        </div>
      )}

      {/* Logo position */}
      {onUpdateStyle && (
        <div className="mt-2 flex items-center gap-2">
          <span className="w-12 shrink-0 text-[10px] text-warm-grey">Logo</span>
          <LogoPositionToggle
            value={content?.title_logo_position ?? "above"}
            onChange={(p) => onUpdateStyle({ title_logo_position: p } as StyleUpdate)}
          />
        </div>
      )}

      {/* Logo size */}
      {onUpdateStyle && (content?.title_logo_position ?? "above") !== "hidden" && (
        <div className="mt-2 flex items-center gap-2">
          <span className="w-12 shrink-0 text-[10px] text-warm-grey">Size</span>
          <LogoSizeToggle
            value={content?.title_logo_size ?? "small"}
            onChange={(s) => onUpdateStyle({ title_logo_size: s } as StyleUpdate)}
          />
        </div>
      )}

      {/* Title transition style */}
      {onUpdateStyle && (
        <div className="mt-3 space-y-1">
          <p className="text-[10px] font-medium uppercase tracking-wider text-warm-grey/70">
            Transition out
          </p>
          <TransitionStyleToggle
            value={content?.title_transition_style ?? "iris"}
            onChange={(s) => onUpdateStyle({ title_transition_style: s })}
          />
        </div>
      )}

      <p className="mt-3 text-[10px] tabular-nums text-warm-grey/60">
        Duration: {titleDuration.toFixed(1)}s
      </p>

      {onUpdateStyle && (
        <StyleControls
          position={content?.title_text_position ?? "bottom"}
          align={content?.title_text_align ?? "center"}
          font={content?.title_text_font}
          size={content?.title_text_size}
          weight={content?.title_text_weight}
          colour={content?.title_text_colour}
          uppercase={content?.title_text_uppercase ?? false}
          onChangePosition={(p) => onUpdateStyle({ text_position: p })}
          onChangeAlign={(a) => onUpdateStyle({ text_align: a })}
          onChangeFont={(f) => onUpdateStyle({ text_font: f })}
          onChangeSize={(s) => onUpdateStyle({ text_size: s })}
          onChangeWeight={(w) => onUpdateStyle({ text_weight: w })}
          onChangeColour={(c) => onUpdateStyle({ text_colour: c })}
          onChangeUppercase={(u) => onUpdateStyle({ text_uppercase: u })}
        />
      )}
    </div>
  )
}

function ContentInspector({
  sceneIndex,
  scene,
  ctaButtonTextColour,
  onUpdateScene,
  onUpdateStyle,
  onApplyStyleToAll,
}: {
  sceneIndex: number
  scene: { text: string; duration_seconds: number; text_position?: TextPosition; text_align?: TextAlign; text_font?: string; text_size?: string; text_colour?: string; text_uppercase?: boolean; text_weight?: string; show_cta?: boolean; show_cta_button?: boolean }
  ctaButtonTextColour?: string
  onUpdateScene: (index: number, text: string) => void
  onUpdateStyle?: (style: StyleUpdate) => void
  onApplyStyleToAll?: (style: StyleUpdate) => void
}) {
  const showCta = scene.show_cta ?? true
  const showCtaButton = scene.show_cta_button ?? true

  return (
    <div className="flex flex-1 flex-col">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-[10px] font-medium uppercase tracking-wider text-warm-grey/70">
          Scene {sceneIndex + 1} — Overlay text
        </p>
        <span className="text-[10px] tabular-nums text-warm-grey/60">
          {scene.duration_seconds.toFixed(1)}s
        </span>
      </div>

      <Textarea
        value={scene.text}
        onChange={(e) => onUpdateScene(sceneIndex, e.target.value)}
        placeholder="Text shown on screen during this scene..."
        className="resize-none text-sm leading-relaxed text-slate"
        rows={2}
      />

      {onUpdateStyle && (
        <StyleControls
          position={scene.text_position ?? "bottom"}
          align={scene.text_align ?? "center"}
          font={scene.text_font}
          size={scene.text_size}
          weight={scene.text_weight}
          colour={scene.text_colour}
          uppercase={scene.text_uppercase ?? false}
          onChangePosition={(p) => onUpdateStyle({ text_position: p })}
          onChangeAlign={(a) => onUpdateStyle({ text_align: a })}
          onChangeFont={(f) => onUpdateStyle({ text_font: f })}
          onChangeSize={(s) => onUpdateStyle({ text_size: s })}
          onChangeWeight={(w) => onUpdateStyle({ text_weight: w })}
          onChangeColour={(c) => onUpdateStyle({ text_colour: c })}
          onChangeUppercase={(u) => onUpdateStyle({ text_uppercase: u })}
          onApplyToAll={onApplyStyleToAll}
        />
      )}

      {onUpdateStyle && (
        <div className="mt-3 space-y-2 border-t border-stone/20 pt-3">
          <p className="text-[10px] font-medium uppercase tracking-wider text-warm-grey/70">
            Branding
          </p>
          <div className="flex items-center gap-2">
            <span className="w-12 shrink-0 text-[10px] text-warm-grey">CTA bar</span>
            <button
              type="button"
              onClick={() => onUpdateStyle({ show_cta: !showCta })}
              className={`rounded-md border px-2 py-1 text-[10px] font-medium transition-colors ${
                showCta
                  ? "border-charcoal bg-charcoal text-white"
                  : "border-stone/30 bg-white text-warm-grey hover:bg-stone/10"
              }`}
            >
              {showCta ? "Visible" : "Hidden"}
            </button>
            {onApplyStyleToAll && <ApplyAllButton onClick={() => onApplyStyleToAll({ show_cta: showCta })} />}
          </div>
          <div className={`flex items-center gap-2 ${showCta ? "" : "opacity-50"}`}>
            <span className="w-12 shrink-0 text-[10px] text-warm-grey">Button</span>
            <button
              type="button"
              disabled={!showCta}
              onClick={() => onUpdateStyle({ show_cta_button: !showCtaButton })}
              className={`rounded-md border px-2 py-1 text-[10px] font-medium transition-colors disabled:cursor-not-allowed ${
                showCtaButton
                  ? "border-charcoal bg-charcoal text-white"
                  : "border-stone/30 bg-white text-warm-grey hover:bg-stone/10"
              }`}
            >
              {showCtaButton ? "Visible" : "Hidden"}
            </button>
            {onApplyStyleToAll && <ApplyAllButton onClick={() => onApplyStyleToAll({ show_cta_button: showCtaButton })} />}
          </div>
          <div className="flex items-center gap-2">
            <span className="w-12 shrink-0 text-[10px] text-warm-grey">CTA text</span>
            <ColourPicker value={ctaButtonTextColour} onChange={(c) => onUpdateStyle({ cta_button_text_colour: c })} />
            {onApplyStyleToAll && <ApplyAllButton onClick={() => onApplyStyleToAll({ cta_button_text_colour: ctaButtonTextColour })} />}
          </div>
        </div>
      )}
    </div>
  )
}

type ClosingStyleUpdate = {
  closing_bg_colour?: string
  closing_bg_gradient?: "none" | "linear-top" | "linear-bottom" | "radial"
  closing_bg_gradient_end?: string
  closing_transition_style?: SpecialTransitionStyle
  closing_text_font?: string
  closing_text_size?: string
  closing_text_weight?: string
  closing_text_colour?: string
  closing_text_uppercase?: boolean
  closing_logo_size?: LogoSize
  show_watermark?: boolean
}

function ClosingInspector({
  content,
  closingDuration,
  closingBgColour,
  brandColour,
  closingFont,
  closingSize,
  closingWeight,
  closingColour,
  closingUppercase,
  onUpdateStyle,
}: {
  content: ScriptContent | undefined
  closingDuration: number
  closingBgColour?: string
  brandColour: string
  closingFont?: string
  closingSize?: string
  closingWeight?: string
  closingColour?: string
  closingUppercase?: boolean
  onUpdateStyle?: (style: ClosingStyleUpdate) => void
}) {
  const activeBgColour = closingBgColour || brandColour

  return (
    <div className="flex flex-1 flex-col">
      <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-warm-grey/70">
        Closing scene
      </p>
      <p className="text-sm text-warm-grey">
        Property details and agent contact information appear automatically.
      </p>
      <p className="mt-3 text-[10px] tabular-nums text-warm-grey/60">
        Duration: {closingDuration.toFixed(1)}s
      </p>

      {/* Background colour */}
      {onUpdateStyle && (
        <div className="mt-3 space-y-2">
          <p className="text-[10px] font-medium uppercase tracking-wider text-warm-grey/70">
            Background
          </p>
          <div className="flex items-center gap-1">
            {["#1A1A1A", brandColour, "#F5F2ED"].map((colour) => (
              <button
                key={colour}
                type="button"
                onClick={() => onUpdateStyle({ closing_bg_colour: colour })}
                className={`size-5 rounded-full border-2 transition-all ${activeBgColour === colour ? "border-charcoal ring-1 ring-charcoal/20" : "border-stone/40 hover:border-stone"}`}
                style={{ backgroundColor: colour }}
                title={colour}
              />
            ))}
            <label className="relative ml-0.5 cursor-pointer">
              <input
                type="color"
                value={activeBgColour}
                onChange={(e) => onUpdateStyle({ closing_bg_colour: e.target.value })}
                className="absolute inset-0 size-0 opacity-0"
              />
              <div
                className={`flex size-5 items-center justify-center rounded-full border-2 transition-all ${
                  activeBgColour && !["#1A1A1A", brandColour, "#F5F2ED"].includes(activeBgColour)
                    ? "border-charcoal ring-1 ring-charcoal/20"
                    : "border-stone/40 hover:border-stone"
                }`}
                style={{
                  background: activeBgColour && !["#1A1A1A", brandColour, "#F5F2ED"].includes(activeBgColour)
                    ? activeBgColour
                    : "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)",
                }}
                title="Custom colour"
              />
            </label>
          </div>
        </div>
      )}

      {/* Gradient style */}
      {onUpdateStyle && (
        <div className="mt-3 flex items-center gap-2">
          <span className="w-12 shrink-0 text-[10px] text-warm-grey">Gradient</span>
          <GradientToggle
            value={content?.closing_bg_gradient ?? "none"}
            onChange={(g) => onUpdateStyle({ closing_bg_gradient: g })}
          />
        </div>
      )}

      {/* Gradient end colour */}
      {onUpdateStyle && content?.closing_bg_gradient && content.closing_bg_gradient !== "none" && (
        <div className="mt-2 space-y-1">
          <p className="text-[10px] font-medium uppercase tracking-wider text-warm-grey/70">
            Gradient end colour
          </p>
          <div className="flex items-center gap-1">
            {["#1A1A1A", "#000000", "#F5F2ED"].map((colour) => (
              <button
                key={colour}
                type="button"
                onClick={() => onUpdateStyle({ closing_bg_gradient_end: colour })}
                className={`size-5 rounded-full border-2 transition-all ${(content?.closing_bg_gradient_end || "#1A1A1A") === colour ? "border-charcoal ring-1 ring-charcoal/20" : "border-stone/40 hover:border-stone"}`}
                style={{ backgroundColor: colour }}
                title={colour}
              />
            ))}
            <label className="relative ml-0.5 cursor-pointer">
              <input
                type="color"
                value={content?.closing_bg_gradient_end || "#1A1A1A"}
                onChange={(e) => onUpdateStyle({ closing_bg_gradient_end: e.target.value })}
                className="absolute inset-0 size-0 opacity-0"
              />
              <div
                className={`flex size-5 items-center justify-center rounded-full border-2 transition-all ${
                  content?.closing_bg_gradient_end && !["#1A1A1A", "#000000", "#F5F2ED"].includes(content.closing_bg_gradient_end)
                    ? "border-charcoal ring-1 ring-charcoal/20"
                    : "border-stone/40 hover:border-stone"
                }`}
                style={{
                  background: content?.closing_bg_gradient_end && !["#1A1A1A", "#000000", "#F5F2ED"].includes(content.closing_bg_gradient_end)
                    ? content.closing_bg_gradient_end
                    : "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)",
                }}
                title="Custom colour"
              />
            </label>
          </div>
        </div>
      )}

      {/* Closing transition style */}
      {onUpdateStyle && (
        <div className="mt-3 space-y-1">
          <p className="text-[10px] font-medium uppercase tracking-wider text-warm-grey/70">
            Transition in
          </p>
          <TransitionStyleToggle
            value={content?.closing_transition_style ?? "fade"}
            onChange={(s) => onUpdateStyle({ closing_transition_style: s })}
          />
        </div>
      )}

      {/* Logo size */}
      {onUpdateStyle && (
        <div className="mt-3 flex items-center gap-2">
          <span className="w-12 shrink-0 text-[10px] text-warm-grey">Logo</span>
          <LogoSizeToggle
            value={content?.closing_logo_size ?? "small"}
            onChange={(s) => onUpdateStyle({ closing_logo_size: s })}
          />
        </div>
      )}

      {/* QuiteRight watermark toggle */}
      {onUpdateStyle && (
        <div className="mt-3 flex items-center gap-2">
          <span className="w-20 shrink-0 text-[10px] text-warm-grey">Watermark</span>
          <button
            type="button"
            onClick={() => onUpdateStyle({ show_watermark: !(content?.show_watermark ?? true) })}
            className={`rounded-md border px-2 py-1 text-[10px] font-medium transition-colors ${
              (content?.show_watermark ?? true)
                ? "border-charcoal bg-charcoal text-white"
                : "border-stone/30 bg-white text-warm-grey hover:bg-stone/10"
            }`}
          >
            {(content?.show_watermark ?? true) ? "Visible" : "Hidden"}
          </button>
        </div>
      )}

      {onUpdateStyle && (
        <StyleControls
          showPosition={false}
          font={closingFont}
          size={closingSize}
          weight={closingWeight}
          colour={closingColour}
          uppercase={closingUppercase ?? false}
          onChangeFont={(f) => onUpdateStyle({ closing_text_font: f })}
          onChangeSize={(s) => onUpdateStyle({ closing_text_size: s })}
          onChangeWeight={(w) => onUpdateStyle({ closing_text_weight: w })}
          onChangeColour={(c) => onUpdateStyle({ closing_text_colour: c })}
          onChangeUppercase={(u) => onUpdateStyle({ closing_text_uppercase: u })}
        />
      )}
    </div>
  )
}

function getPreviewMedia(
  sceneIndex: number,
  sceneAssets: SceneAsset[],
  imageUrls: string[],
): { type: "video" | "image"; url: string } | null {
  const animatedAsset = sceneAssets.find(
    (sa) => sa.scene_index === sceneIndex && sa.asset_type === "animated_clip" && sa.status === "completed",
  )
  if (animatedAsset?.file_url) return { type: "video", url: animatedAsset.file_url }

  const enhancedAsset = sceneAssets.find(
    (sa) => sa.scene_index === sceneIndex && sa.asset_type === "enhanced_image" && sa.status === "completed",
  )
  if (enhancedAsset?.file_url) return { type: "image", url: enhancedAsset.file_url }

  if (imageUrls[sceneIndex]) return { type: "image", url: imageUrls[sceneIndex] }

  return null
}
